// Calendar and clock arithmetic for the date/time calculators. Dates are handled as local
// calendar days at noon, so daylight-saving changes never shift a day count.

export function parseDate(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  return Number.isNaN(d.getTime()) ? null : d;
}

export const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const dayDiff = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86_400_000);

export function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6;

/** Business days from start to end, counting both ends, skipping weekends and holidays. */
export function businessDays(
  start: Date,
  end: Date,
  holidays: Set<string>,
): { business: number; weekend: number; holiday: number } {
  const [a, b] = start <= end ? [start, end] : [end, start];
  let business = 0;
  let weekend = 0;
  let holiday = 0;
  for (let d = new Date(a); d <= b; d = addDays(d, 1)) {
    if (isWeekend(d)) weekend += 1;
    else if (holidays.has(toISO(d))) holiday += 1;
    else business += 1;
  }
  return { business, weekend, holiday };
}

/** Add (or subtract) business days, skipping weekends and holidays. */
export function addBusinessDays(d: Date, n: number, holidays: Set<string>): Date {
  let out = new Date(d);
  const step = n < 0 ? -1 : 1;
  let left = Math.abs(n);
  while (left > 0) {
    out = addDays(out, step);
    if (!isWeekend(out) && !holidays.has(toISO(out))) left -= 1;
  }
  return out;
}

/** Whole years, months and days from a to b (calendar-style, like an age). */
export function ymd(a: Date, b: Date): { years: number; months: number; days: number } {
  const [from, to] = a <= b ? [a, b] : [b, a];
  let years = to.getFullYear() - from.getFullYear();
  let months = to.getMonth() - from.getMonth();
  let days = to.getDate() - from.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(to.getFullYear(), to.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years, months, days };
}

export function isoWeek(d: Date): number {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

/** "08:30" → minutes after midnight. */
export function parseClock(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

/** "1:45", "1h 45m", "105m" or "1.75" → minutes (durations may exceed 24 h). */
export function parseDuration(value: string): number | null {
  const s = value.trim().toLowerCase();
  if (!s) return null;
  let m = /^(-)?(\d+):(\d{1,2})(?::(\d{1,2}))?$/.exec(s);
  if (m) return (m[1] ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]) + Number(m[4] ?? 0) / 60);
  m = /^(-)?(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+(?:\.\d+)?)\s*m(?:in)?)?$/.exec(s);
  if (m && (m[2] || m[3])) return (m[1] ? -1 : 1) * (Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0));
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s) * 60;
  return null;
}

export function formatHM(minutes: number): string {
  const sign = minutes < 0 ? '−' : '';
  const total = Math.round(Math.abs(minutes));
  return `${sign}${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Minutes worked between two clock times, across midnight if needed, minus a break. */
export function shiftMinutes(start: number, end: number, breakMin: number): number {
  const span = end >= start ? end - start : end + 1440 - start;
  return Math.max(0, span - breakMin);
}

function nthWeekday(year: number, month: number, weekday: number, n: number): Date {
  // n > 0: nth weekday of the month; n = -1: the last one.
  if (n > 0) {
    const first = new Date(year, month, 1, 12);
    return new Date(year, month, 1 + ((weekday - first.getDay() + 7) % 7) + (n - 1) * 7, 12);
  }
  const last = new Date(year, month + 1, 0, 12);
  return new Date(year, month, last.getDate() - ((last.getDay() - weekday + 7) % 7), 12);
}

/** US federal holidays as observed (Saturday → Friday, Sunday → Monday), 5 U.S.C. 6103. */
export function usFederalHolidays(year: number): Array<{ date: Date; name: string }> {
  const fixed = (m: number, d: number, name: string) => {
    const date = new Date(year, m, d, 12);
    const day = date.getDay();
    return { date: day === 6 ? addDays(date, -1) : day === 0 ? addDays(date, 1) : date, name };
  };
  return [
    fixed(0, 1, "New Year's Day"),
    { date: nthWeekday(year, 0, 1, 3), name: 'Martin Luther King Jr. Day' },
    { date: nthWeekday(year, 1, 1, 3), name: "Washington's Birthday" },
    { date: nthWeekday(year, 4, 1, -1), name: 'Memorial Day' },
    fixed(5, 19, 'Juneteenth'),
    fixed(6, 4, 'Independence Day'),
    { date: nthWeekday(year, 8, 1, 1), name: 'Labor Day' },
    { date: nthWeekday(year, 9, 1, 2), name: 'Columbus Day' },
    fixed(10, 11, 'Veterans Day'),
    { date: nthWeekday(year, 10, 4, 4), name: 'Thanksgiving Day' },
    fixed(11, 25, 'Christmas Day'),
  ];
}
